import { useState, useEffect, useRef, useMemo } from "react";
import {
  TextField,
  Box,
  Autocomplete,
} from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import axios from "axios";
import { useNavigate, useLocation } from "react-router-dom";
import { useSnackbar } from "../../providers/SnackBarProvider";
import { useAppBar } from "../../providers/AppBarProvider";
import EditPage from "../../pages/edit/EditPage";
import dayjs from 'dayjs';
import 'dayjs/locale/es';
import 'dayjs/locale/pt';
import 'dayjs/locale/en-gb';
import {
  handleError,
  validateNonEmpty,
  fetchFinanceTypesPayments,
  removeNullsAndEmptys,
  resolveDateFormattingLocale,
  bcp47ToDayjsLocale,
  getDayjsDateTimeFormatForLocale,
  resolveHour12Preference,
} from "../../utils/lib";
import { useTranslation } from 'react-i18next';
import { useConfig } from "../../providers/ConfigProvider";

const EditSpending = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const snackbar = useSnackbar();
  const {updateTitle, toggleFab} = useAppBar();
  const { config, isLoadingConfig } = useConfig();
  const [isLoading, setIsLoading] = useState(true);

  const dateFormattingLocale = useMemo(() => resolveDateFormattingLocale(config), [config]);
  const useHour12 = useMemo(
    () => resolveHour12Preference(config, dateFormattingLocale),
    [config, dateFormattingLocale]
  );
  const dateTimePickerFormat = useMemo(
    () => getDayjsDateTimeFormatForLocale(dateFormattingLocale, useHour12),
    [dateFormattingLocale, useHour12]
  );
  const dayjsPickerLocale = useMemo(
    () => bcp47ToDayjsLocale(dateFormattingLocale),
    [dateFormattingLocale]
  );
  
  const [spending, setSpending] = useState({
    date: dayjs(),
    amount: "",
    finance_payment_method: null,
    reason: "",
  });

  const [financePaymentMethods, setFinancePaymentMethods] = useState([]);

  const [errors, setErrors] = useState({});
  const [refs] = useState({
    amount: useRef(null),
    finance_payment_method: useRef(null),
    date: useRef(null),
    reason: useRef(null),
  });  

  useEffect(() => {
    
    updateTitle(
      '',
      t('edit.spendingTitle'),
      t('common.completeData')
    );

    toggleFab(false);

    const fetchData = async () => {
      setIsLoading(true);
      try {
        const financeTypesPayments = await fetchFinanceTypesPayments();
        //console.log(financeTypesPayments);
        setFinancePaymentMethods(financeTypesPayments);
      } catch (error) {
        handleError(error, snackbar);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();

    // Add a delay to focus to ensure component is fully rendered
    setTimeout(() => {
      if (refs.amount.current) {
        refs.amount.current.focus();
      }
    }, 100);
  }, []);

  // Set default payment method to 'Efectivo' when financePaymentMethods are loaded
  useEffect(() => {
    if (financePaymentMethods.length > 0) {
      const efectivoMethod = financePaymentMethods.find(method => method.name === 'Efectivo');
      if (efectivoMethod) {
        setSpending(prev => ({
          ...prev,
          finance_payment_method: efectivoMethod
        }));
      }
    }
  }, [financePaymentMethods]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setSpending({ ...spending, [name]: value });
  };

  const handleKeyDown = (e, fieldName) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      
      const fieldOrder = ['date', 'amount', 'finance_payment_method', 'reason'];
      const currentIndex = fieldOrder.indexOf(fieldName);
      
      if (currentIndex === fieldOrder.length - 1) {
        // Last field - submit the form
        handleSubmit(e);
      } else if (currentIndex >= 0 && currentIndex < fieldOrder.length - 1) {
        // Move to the next field
        const nextField = fieldOrder[currentIndex + 1];
        if (refs[nextField] && refs[nextField].current) {
          refs[nextField].current.focus();
        }
      }
    }
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();

    if (
      !validateNonEmpty("amount", spending.amount, setErrors, refs.amount) ||
      !validateNonEmpty("finance_payment_method", spending.finance_payment_method?.name, setErrors, refs.finance_payment_method)
    ) {
      return;
    }

    save();
  };

  const save = async () => {  
    setIsLoading(true);

    const dataToSend = {
      ...removeNullsAndEmptys(spending),
      date: spending.date.format('YYYY-MM-DD HH:mm:ss')
    };

    //console.log(dataToSend);
    try {
      let response = await axios.post(
        `${import.meta.env.VITE_API_URL}spendings`,
        dataToSend,
        { withCredentials: true }
      );

      //console.log(response);
      navigate(-1);
    } catch (error) {
      handleError(error, snackbar);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <EditPage 
      onSubmit={handleSubmit} 
      isLoading={isLoading || isLoadingConfig}
    >
      <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={dayjsPickerLocale}>
        <DateTimePicker
          label={t('common.dateTime')}
          value={spending.date}
          onChange={(newValue) => setSpending({ ...spending, date: newValue })}
          slotProps={{ 
            textField: { 
              fullWidth: true,
              margin: "normal",
              inputRef: refs.date,
              onKeyDown: (e) => handleKeyDown(e, 'date')
            } 
          }}
          format={dateTimePickerFormat}
          ampm={useHour12}
        />
      </LocalizationProvider>

      <TextField
        fullWidth
        margin="normal"
        label={t('common.amount')}
        type="text"
        inputProps={{ inputMode: "decimal", pattern: "[0-9]*[.,]?[0-9]*" }}
        value={spending.amount}
        onChange={(e) => {
          const value = e.target.value.replace(/[^0-9.,]/g, "");
          setSpending({ ...spending, amount: value });
        }}
        inputRef={refs.amount}
        error={!!errors.amount}
        helperText={errors.amount}
        onKeyDown={(e) => handleKeyDown(e, 'amount')}
      />

      <Autocomplete
        fullWidth
        options={financePaymentMethods}
        getOptionLabel={(option) => (option ? option.name : "")}
        value={spending.finance_payment_method}
        disableClearable={true}
        onChange={(e, newValue) =>
          setSpending({
            ...spending,
            finance_payment_method: newValue
          })
        }
        renderInput={(params) => (
          <TextField
            {...params}
            label={t('common.paymentMethod')}
            name="finance_payment_method"
            margin="normal"
            inputRef={refs.finance_payment_method}
            error={!!errors.finance_payment_method}
            helperText={errors.finance_payment_method}
            onKeyDown={(e) => handleKeyDown(e, 'finance_payment_method')}
          />
        )}
      />

      <TextField
        fullWidth
        margin="normal"
        label={t('common.motive')}
        name="reason"
        value={spending.reason}
        onChange={handleChange}
        error={!!errors.reason}
        helperText={errors.reason}
        inputRef={refs.reason}
        inputProps={{ maxLength: 120 }}
        onKeyDown={(e) => handleKeyDown(e, 'reason')}
        sx={{ mb: 2 }}
      />
    </EditPage>
  );
};

export default EditSpending;
